import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../store/onboardingStore';
import { OnboardingContainer } from '../components/onboarding/OnboardingContainer';
import {
  Step1BusinessInfo,
  Step2ContactInfo,
  Step3DocumentUpload,
  Step4ACHSetup,
  Step5ReviewSignature,
} from '../components/onboarding/steps';
import { SuccessPage } from '../components/onboarding/SuccessPage';
import { onboardingApi } from '../services/onboardingApi';

export const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentStep,
    formData,
    applicationId,
    uploadedFiles,
    setSubmitting,
    setSuccess,
    resetForm,
    setError,
    clearErrors,
  } = useOnboardingStore();

  // Note: Onboarding page is accessible without authentication
  // Users can fill out the form and submit for admin approval
  // Account creation happens only after admin approval

  const handleSuccess = (newApplicationId: string) => {
    setSuccess(`Your application has been submitted successfully!`, newApplicationId);
    setSubmitting(false);
    // Form data will be cleared when user navigates away from success page
  };

  const handleSubmitApplication = async () => {
    setSubmitting(true);
    clearErrors();

    try {
      // Pass the entire uploaded files array with File objects
      const response = await onboardingApi.submitApplication(
        formData,
        uploadedFiles
      );

      // Handle successful submission
      handleSuccess(response.applicationId);
    } catch (error) {
      console.error('Application submission failed:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to submit application. Please try again.';
      setError('submission', errorMessage);
      setSubmitting(false);
    }
  };

  // Render success page if submission was successful
  if (applicationId) {
    return (
      <SuccessPage
        applicationId={applicationId}
        contactEmail={formData.contactEmail}
        onDashboardClick={() => {
          resetForm();
          navigate('/dashboard');
        }}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1BusinessInfo />;
      case 2:
        return <Step2ContactInfo />;
      case 3:
        return <Step3DocumentUpload />;
      case 4:
        return <Step4ACHSetup />;
      case 5:
        return <Step5ReviewSignature />;
      default:
        return null;
    }
  };

  return (
    <OnboardingContainer
      onSuccess={handleSuccess}
      onSubmit={handleSubmitApplication}
    >
      {renderStepContent()}
    </OnboardingContainer>
  );
};
